/**
 * Doprava termínu tak, jak ji kreslí kalendář: pro každý směr **hlavní doprava** (jeden box na leg)
 * a **navazující linky** (spojené lety / svozy).
 *
 * Čte se z kalendářového read modelu (`CalendarEvent`), který nese rozřešené legy schématu
 * (`schema.primaryLegs` / `schema.connectedLegs`) a autobusovou stranu termínu (`termTransport`).
 * Nic se tu nedopočítává z domény — jen se překládá do tvaru, který layout pruhu umí naskládat.
 *
 * ## Odkud se bere který stav boxu
 *
 * | stav      | kdy nastane                                              | jak vypadá                  |
 * | --------- | -------------------------------------------------------- | --------------------------- |
 * | `full`    | známé oba kódy i oba časy                                | plný rámeček                |
 * | `partial` | chybí čas odjezdu nebo příjezdu                          | čárkovaný, tlumený text     |
 * | `draft`   | chybí kód jedné strany (typicky nepřiřazená linka svozu) | čárkovaný, `--phx-draft`    |
 * | `missing` | směr nemá žádný leg — spoj není přiřazený                | čárkovaný, `--phx-danger`   |
 *
 * ## Čas jako offset
 *
 * `departureMinutes` / `arrivalMinutes` (i `timeMinutes` autobusové strany) jsou **offsety v minutách
 * vůči datu cesty**, ne čas v rámci dne. Můžou tedy být záporné i větší než 1440 — z toho se odvodí
 * jak popisek `HH:MM`, tak posun dne (`−1d` / `+1d`), který kalendář ukazuje u času a v režimu
 * „Reálná doprava“ podle něj boxy usazuje na skutečné dny.
 */
import type {
  CalendarConnectedLegResponse,
  CalendarPrimaryLegResponse,
  TransportSchemeKind,
} from '@/api/generated/schemas';
import type { CalendarEvent } from '../calendarModel';

/** Čím se leg jede — rozhoduje o ikoně boxu. */
export type TransportLegMode = 'air' | 'bus';

/** Směr cesty tak, jak ho zná layout pruhu. */
export type TransportLegDirection = 'out' | 'back';

/** Vyplněnost boxu dopravy. Viz tabulka v hlavičce souboru. */
export type TransportLegState = 'full' | 'partial' | 'draft' | 'missing';

/** Čas spoje: popisek v rámci dne + posun dne vůči datu termínu. */
export interface LegTime {
  /** `HH:MM` */
  label: string;
  /** −1 = předchozí den, +1 = následující den, 0 = den termínu. */
  dayOffset: number;
}

/** Jeden box dopravy — hlavní leg nebo navazující linka. */
export interface TransportLegView {
  key: string;
  direction: TransportLegDirection;
  /** `primary` = hlavní doprava, `feeder` = navazující linka (menší box s půlkolečkem). */
  kind: 'primary' | 'feeder';
  mode: TransportLegMode;
  state: TransportLegState;
  /** Odkud — `null` znamená neznámý bod (vykreslí se `?`). */
  from: string | null;
  /** Kam — `null` znamená neznámý bod. */
  to: string | null;
  departure: LegTime | null;
  arrival: LegTime | null;
}

/** Doprava jednoho směru: hlavní legy + navazující linky. */
export interface TermDirectionPlan {
  primary: TransportLegView[];
  feeders: TransportLegView[];
}

/** Doprava termínu pro obě cesty. */
export interface TermTransportPlan {
  out: TermDirectionPlan;
  back: TermDirectionPlan;
}

const MINUTES_PER_DAY = 1440;

/** Jakou dopravou se jede daný směr — plyne z druhu schématu, ne z legu samotného. */
export function modeForDirection(
  kind: TransportSchemeKind,
  direction: TransportLegDirection
): TransportLegMode {
  switch (kind) {
    case 'Air':
      return 'air';
    case 'Bus':
      return 'bus';
    case 'AirBus':
      return direction === 'out' ? 'air' : 'bus';
    default:
      return direction === 'out' ? 'bus' : 'air';
  }
}

/** Offset v minutách → `HH:MM` + posun dne. */
export function toLegTime(minutes: number | null | undefined): LegTime | null {
  if (minutes === null || minutes === undefined) return null;
  const dayOffset = Math.floor(minutes / MINUTES_PER_DAY);
  const withinDay = minutes - dayOffset * MINUTES_PER_DAY;
  const hours = Math.floor(withinDay / 60);
  const mins = withinDay % 60;
  return {
    label: `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`,
    dayOffset,
  };
}

function stateOf(leg: {
  from: string | null;
  to: string | null;
  departure: LegTime | null;
  arrival: LegTime | null;
}): TransportLegState {
  if (!leg.from || !leg.to) return 'draft';
  if (!leg.departure || !leg.arrival) return 'partial';
  return 'full';
}

function view(
  partial: Omit<TransportLegView, 'state'> & { state?: TransportLegState }
): TransportLegView {
  return { ...partial, state: partial.state ?? stateOf(partial) };
}

/** Box „spoj není přiřazen“ — směr nemá žádný leg, takže neznáme ani stanice. */
function missingLeg(
  key: string,
  direction: TransportLegDirection,
  mode: TransportLegMode
): TransportLegView {
  return {
    key,
    direction,
    kind: 'primary',
    mode,
    state: 'missing',
    from: null,
    to: null,
    departure: null,
    arrival: null,
  };
}

function primaryFromLeg(
  leg: CalendarPrimaryLegResponse,
  direction: TransportLegDirection,
  mode: TransportLegMode,
  index: number,
  termId: string
): TransportLegView {
  return view({
    key: `${termId}-p${direction}${index}`,
    direction,
    kind: 'primary',
    mode,
    from: leg.fromCode,
    to: leg.toCode,
    departure: toLegTime(leg.departureMinutes),
    arrival: toLegTime(leg.arrivalMinutes),
  });
}

function feederFromLeg(
  leg: CalendarConnectedLegResponse,
  direction: TransportLegDirection,
  index: number,
  termId: string
): TransportLegView {
  return view({
    key: `${termId}-f${direction}${index}`,
    direction,
    kind: 'feeder',
    // Legacy předpis linky nerozlišuje let od autobusu; jediné vodítko je číslo letu.
    mode: leg.flightCode ? 'air' : 'bus',
    from: leg.fromCode,
    to: leg.toCode,
    departure: toLegTime(leg.departureMinutes),
    arrival: toLegTime(leg.arrivalMinutes),
  });
}

/**
 * Autobusová strana schématu nemá legy — má jen stanici na destinaci, čas a přiřazenou linku
 * (svoz z/do výchozí stanice). Kalendář z toho staví jeden box hlavní dopravy: linka → stanice
 * u cesty tam, stanice → linka u cesty zpět. Nepřiřazená linka je právě ten `draft` stav.
 */
function busPrimary(
  event: CalendarEvent,
  direction: TransportLegDirection
): TransportLegView | null {
  const side = direction === 'out' ? event.termTransport.outbound : event.termTransport.inbound;
  if (!side) return null;
  const time = toLegTime(side.timeMinutes);
  const station = side.stationName;
  const line = side.stationChainName;
  return view({
    key: `${event.termId}-p${direction}bus`,
    direction,
    kind: 'primary',
    mode: 'bus',
    from: direction === 'out' ? line : station,
    to: direction === 'out' ? station : line,
    departure: direction === 'out' ? null : time,
    arrival: direction === 'out' ? time : null,
  });
}

function directionPlan(event: CalendarEvent, direction: TransportLegDirection): TermDirectionPlan {
  const schema = event.schema;
  const apiDirection = direction === 'out' ? 'Outbound' : 'Inbound';
  const mode = schema ? modeForDirection(schema.kind, direction) : 'air';

  const feeders = (schema?.connectedLegs ?? [])
    .filter((leg) => leg.direction === apiDirection)
    .map((leg, index) => feederFromLeg(leg, direction, index, event.termId));

  if (mode === 'bus') {
    const bus = busPrimary(event, direction);
    return {
      primary: bus ? [bus] : [missingLeg(`${event.termId}-m${direction}`, direction, 'bus')],
      feeders,
    };
  }

  const legs = (schema?.primaryLegs ?? [])
    .filter((leg) => leg.direction === apiDirection)
    .sort((a, b) => a.sequence - b.sequence)
    .map((leg, index) => primaryFromLeg(leg, direction, mode, index, event.termId));

  if (legs.length === 0) {
    return { primary: [missingLeg(`${event.termId}-m${direction}`, direction, mode)], feeders };
  }
  return { primary: legs, feeders };
}

/** Doprava termínu pro obě cesty — vstup pro layout pruhu. */
export function buildTermTransportPlan(event: CalendarEvent): TermTransportPlan {
  return { out: directionPlan(event, 'out'), back: directionPlan(event, 'back') };
}
